import { useState, useMemo, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Edit, Printer, RefreshCw, LogOut, User, Shield, Settings, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useOrders, useUnapprovedOrders } from "@/hooks/useOrders";
import { useAuth } from "@/contexts/AuthContext";
import { Order } from "@/lib/supabase";
import { OrderEntryWizard } from "@/components/OrderEntryWizard";
import { OrderPrintReport } from "@/components/OrderPrintReport";
import { InfusionForm } from "@/components/InfusionForm";
import { format } from "date-fns";
import { TestEmailButton } from "@/components/TestEmailButton";

export default function PatientList() {
  console.log('PatientList render');
  const [searchMRN, setSearchMRN] = useState("");
  const [searchLastName, setSearchLastName] = useState("");
  const [searchFirstName, setSearchFirstName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showOrderWizard, setShowOrderWizard] = useState(false);
  const [showPrintReport, setShowPrintReport] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [isInboxCollapsed, setIsInboxCollapsed] = useState(false);
  const [showInfusionForm, setShowInfusionForm] = useState(false);
  const [infusionOrderId, setInfusionOrderId] = useState<string>('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  
  const { toast } = useToast();
  const { user, logout } = useAuth();
  
  console.log('About to create filters useMemo');
  
  const filters = useMemo(() => {
    console.log('filters useMemo recalculating', { searchMRN, searchLastName, searchFirstName, selectedStatus });
    return {
      mrn: searchMRN,
      lastName: searchLastName,
      firstName: searchFirstName,
      status: selectedStatus === "all" ? undefined : selectedStatus
    };
  }, [searchMRN, searchLastName, searchFirstName, selectedStatus]);
  
  console.log('About to call useOrders hook');
  const { orders, loading, refetch, updateOrderStatus } = useOrders(filters);
  console.log('About to call useUnapprovedOrders hook');
  const { orders: unapprovedOrders, approveOrder } = useUnapprovedOrders();

  // Pagination calculations
  const totalRecords = orders.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentOrders = orders.slice(startIndex, endIndex);

  // Reset to first page when search filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchMRN, searchLastName, searchFirstName, selectedStatus]);

  const getStatusBadge = (status: string) => {
    const statusMap = {
      "pending": "Unapproved",
      "unapproved": "Unapproved",
      "approved": "New Order Placed", 
      "new_order_placed": "New Order Placed",
      "in_progress": "New Order Received",
      "new_order_received": "New Order Received",
      "completed": "Results Final",
      "results_final": "Results Final",
      "infusion_ordered": "Infusion Ordered",
      "infusion_shipped": "Infusion Shipped",
      "infused_completed": "Infused Completed",
      "cancelled": "Cancelled",
      "ordered": "Ordered",
      "shipped": "Shipped", 
      "infused": "Infused"
    };
    
    const displayStatus = statusMap[status as keyof typeof statusMap] || status;
    const variants = {
      "Unapproved": "destructive",
      "New Order Placed": "secondary", 
      "New Order Received": "outline",
      "Preliminary Results Ready": "secondary",
      "Results Final": "default",
      "Infusion Ordered": "secondary",
      "Infusion Shipped": "outline",
      "Infused Completed": "default",
      "Infusion Order Placed": "outline",
      "Ordered": "secondary",
      "Shipped": "default",
      "Infused": "default",
      "Cancelled": "destructive"
    } as const;
    
    return (
      <Badge variant={variants[displayStatus as keyof typeof variants] || "secondary"}>
        {displayStatus}
      </Badge>
    );
  };

  const handleSearch = () => {
    refetch();
    toast({
      title: "Search completed",
      description: "Order records refreshed",
    });
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not scheduled';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const handlePrintOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowPrintReport(true);
  };

  const handleEditOrder = (order: Order) => {
    // Check if user has permission to edit this order
    if (!canEditOrder(order)) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to edit this order",
        variant: "destructive"
      });
      return;
    }
    
    setEditingOrder(order);
    setShowOrderWizard(true);
  };

  // Function to check if current user can edit an order
  const canEditOrder = useCallback((order: Order) => {
    if (!user) {
      console.log('canEditOrder: No user found');
      return false;
    }
    
    const userRole = user.role;
    const orderStatus = order.order_status;
    
    // Clinicians can only edit orders with 'unapproved' status or no status
    if (userRole === 'clinician') {
      const canEdit = orderStatus === 'unapproved' || orderStatus === null || orderStatus === undefined;
      return canEdit;
    }
    
    // Physicians can edit unapproved orders
    if (userRole === 'physician') {
      const canEdit = orderStatus === 'unapproved';
      console.log('Physician edit check:', { orderStatus, canEdit, orderId: order.id });
      return canEdit;
    }
    
    // Supervisors and admins can edit approved orders and beyond
    if (userRole === 'supervisor' || userRole === 'admin') {
      console.log('Supervisor/Admin can edit any order');
      return true; // Can edit any status
    }
    
    console.log('No role match, denying edit access');
    return false;
  }, [user]);

  const handleNewOrder = () => {
    setEditingOrder(null);
    setShowOrderWizard(true);
  };

  const handleOrderWizardComplete = () => {
    refetch();
  };

  const handleClear = () => {
    setSearchMRN("");
    setSearchLastName("");
    setSearchFirstName("");
    setSelectedStatus("all");
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    refetch();
    setCurrentPage(1);
    toast({
      title: "Data refreshed",
      description: "All records updated",
    });
  };

  const handleLogout = async () => {
    await logout();
  };

  // Function to get available status options based on user role and current status
  const getStatusOptions = useCallback((currentStatus: string, userRole: string) => {
    if (userRole === 'supervisor' || userRole === 'admin') {
      // Supervisors cannot change status if order is still unapproved (awaiting physician approval)
      if (currentStatus === 'unapproved') {
        return []; // No dropdown - must wait for physician approval
      }
      
      // After physician approval, supervisors can manage the workflow
      // Show supervisor-controlled statuses using actual database values
      return [
        { value: 'new_order_placed', label: 'New Order Placed' },
        { value: 'new_order_received', label: 'New Order Received' },
        { value: 'results_final', label: 'Results Final' },
        { value: 'infusion_ordered', label: 'Infusion Ordered' },
        { value: 'infusion_shipped', label: 'Infusion Shipped' },
        { value: 'infusion_completed', label: 'Infusion Completed' },
        { value: 'cancelled', label: 'Cancelled' }
      ];
    } else if (userRole === 'clinician') {
      // Clinicians should NOT be able to edit order status at all
      // They can only edit unit status for shipped units (handled elsewhere)
      return [];
    }
    
    // For all other cases, return empty array (no dropdown, just display current status)
    return [];
  }, []);

  const handleStatusChange = useCallback(async (orderId: string, newStatus: Order['order_status'] | 'shipping') => {
    // Handle special case: when supervisor selects "shipping", it becomes "infusion_shipped"
    let actualStatus: Order['order_status'] = newStatus as Order['order_status'];
    if (newStatus === 'shipping') {
      actualStatus = 'infusion_shipped'; // Use the actual status type that exists
    }
    
    // Check permissions based on status change
    if (actualStatus === 'infusion_shipped' && user?.role !== 'supervisor' && user?.role !== 'admin') {
      toast({
        title: "Access Denied",
        description: "Only supervisors can mark orders as shipped",
        variant: "destructive"
      });
      return;
    }
    
    if (actualStatus === 'infusion_completed' && user?.role !== 'clinician' && user?.role !== 'admin') {
      toast({
        title: "Access Denied", 
        description: "Only clinicians can mark orders as infused",
        variant: "destructive"
      });
      return;
    }
    
    if (user?.role !== 'supervisor' && user?.role !== 'admin' && user?.role !== 'clinician') {
      toast({
        title: "Access Denied",
        description: "You don't have permission to change order status",
        variant: "destructive"
      });
      return;
    }
    
    await updateOrderStatus(orderId, actualStatus);
  }, [user?.role, updateOrderStatus, toast]);

  const handleApproveOrder = async (orderId: string) => {
    if (user?.role !== 'physician' && user?.role !== 'admin') {
      toast({
        title: "Access Denied", 
        description: "Only physicians can approve orders",
        variant: "destructive"
      });
      return;
    }
    
    // Open the order for review in the wizard
    const orderToReview = unapprovedOrders.find(order => order.id === orderId);
    if (orderToReview) {
      setEditingOrder(orderToReview);
      setShowOrderWizard(true);
    }
  };

  const handleStatusClick = (order: Order) => {
    // ENSURE OrderEntryWizard is closed first
    setShowOrderWizard(false);
    setEditingOrder(null);
    
    // Allow access for orders from new_order_received onwards
    if (order.order_status === 'new_order_received' || 
        order.order_status === 'results_final' ||
        order.order_status === 'infusion_ordered' ||
        order.order_status === 'infusion_shipped' ||
        order.order_status === 'infusion_completed') {
      setInfusionOrderId(order.id);
      setShowInfusionForm(true);
    }
  };

  const handleInfusionFormSubmit = () => {
    refetch(); // Refresh orders to show updated unit statuses
  };

  // Memoize order status lookup to prevent infinite re-renders
  const currentOrderStatus = useMemo(() => {
    const order = orders.find(o => o.id === infusionOrderId);
    return order?.order_status || '';
  }, [orders, infusionOrderId]);

  // Only show inbox for physicians and admin
  const showInbox = user?.role === 'physician' || user?.role === 'admin';

  return (
    <div className="min-h-screen bg-gradient-medical">
      {/* Header */}
      <header className="bg-white shadow-card border-b">
        <div className="container mx-auto px-6 py-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/lovable-uploads/5a6285bd-aaa9-4c5f-8b8f-a7c3b2e2f4d4.png" 
                alt="PragLife - Pragmatrix Lifesciences"
                className="h-12 w-auto"
              />
              <h1 className="text-2xl font-bold text-medical-blue">Cellular Ordering System</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" className="text-medical-blue hover:text-medical-blue-dark">
                <User className="h-4 w-4 mr-2" />
                {user?.first_name} {user?.last_name}
              </Button>
              <Button variant="ghost" size="sm" className="text-medical-blue hover:text-medical-blue-dark">
                <Shield className="h-4 w-4 mr-2" />
                {user?.role}
              </Button>
              <Button variant="ghost" size="sm" className="text-medical-blue hover:text-medical-blue-dark">
                <Settings className="h-4 w-4 mr-2" />
                <Link to="/settings" className="text-inherit no-underline">Settings</Link>
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-medical-blue hover:text-medical-blue-dark"
                onClick={handleRefresh}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-medical-blue text-medical-blue hover:bg-medical-blue-light"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Physician Inbox - Only shown for physician role */}
        {showInbox && unapprovedOrders.length > 0 && (
          <Card className="mb-8 bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-l-destructive">
            <CardHeader>
              <CardTitle 
                className="text-destructive flex items-center justify-between cursor-pointer"
                onClick={() => setIsInboxCollapsed(!isInboxCollapsed)}
              >
                <div className="flex items-center">
                  <Badge variant="destructive" className="mr-3">Urgent</Badge>
                  Physician Inbox - Unapproved Orders ({unapprovedOrders.length})
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive-foreground hover:bg-destructive/10 p-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsInboxCollapsed(!isInboxCollapsed);
                  }}
                >
                  {isInboxCollapsed ? (
                    <ChevronDown className="h-6 w-6" />
                  ) : (
                    <ChevronUp className="h-6 w-6" />
                  )}
                </Button>
              </CardTitle>
            </CardHeader>
            {!isInboxCollapsed && (
              <CardContent>
                <div className="space-y-2">
                  {unapprovedOrders.map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-destructive/20">
                      <div>
                        <span className="font-medium">
                          MRN: {order.patients?.mrn} - {order.patients?.last_name}, {order.patients?.first_name}
                        </span>
                        <p className="text-sm text-muted-foreground">
                          Order Date: {formatDate(order.order_date)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Diagnosis: {order.diagnosis}
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-gradient-primary"
                        onClick={() => handleApproveOrder(order.id)}
                      >
                        Review & Approve
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Search Panel */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-medical-blue flex items-center justify-between">
              <span>Order Search</span>
              <div className="flex items-center space-x-2">
                <div className="hidden">
                  <TestEmailButton />
                </div>
                {user?.role !== 'supervisor' && (
                  <Button onClick={handleNewOrder} className="bg-gradient-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    New Order
                  </Button>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
              <div>
                <Label htmlFor="mrn">MRN</Label>
                <Input
                  id="mrn"
                  value={searchMRN}
                  onChange={(e) => setSearchMRN(e.target.value)}
                  placeholder="Enter MRN"
                  className="border-medical-blue/20 focus:border-medical-blue"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={searchLastName}
                  onChange={(e) => setSearchLastName(e.target.value)}
                  placeholder="Enter last name"
                  className="border-medical-blue/20 focus:border-medical-blue"
                />
              </div>
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={searchFirstName}
                  onChange={(e) => setSearchFirstName(e.target.value)}
                  placeholder="Enter first name"
                  className="border-medical-blue/20 focus:border-medical-blue"
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="border-medical-blue/20 focus:border-medical-blue">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="unapproved">Unapproved</SelectItem>
                    <SelectItem value="new_order_placed">New Order Placed</SelectItem>
                    <SelectItem value="new_order_received">New Order Received</SelectItem>
                    <SelectItem value="results_final">Results Final</SelectItem>
                    <SelectItem value="infusion_ordered">Infusion Ordered</SelectItem>
                    <SelectItem value="infusion_shipped">Infusion Shipped</SelectItem>
                    <SelectItem value="infusion_completed">Infusion Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="department">Department</Label>
                <Select>
                  <SelectTrigger className="border-medical-blue/20 focus:border-medical-blue">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oncology">Oncology</SelectItem>
                    <SelectItem value="hematology">Hematology</SelectItem>
                    <SelectItem value="pediatrics">Pediatrics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <Button onClick={handleSearch} className="bg-gradient-primary" disabled={loading}>
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
              <Button variant="outline" onClick={handleClear} className="border-medical-blue text-medical-blue hover:bg-medical-blue-light">
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Patient Data Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="text-medical-blue flex items-center justify-between">
              <span>Orders ({totalRecords})</span>
              <div className="text-sm font-normal text-muted-foreground">
                Page {currentPage} of {totalPages} ({recordsPerPage} per page)
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col min-h-[600px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin text-medical-blue" />
                <span className="ml-2">Loading orders...</span>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-x-auto min-h-[480px]">
                  <table className="w-full border-collapse table-fixed">
                    <thead>
                      <tr className="border-b border-medical-blue/20">
                        <th className="text-left p-4 font-semibold text-medical-blue w-28">MRN</th>
                        <th className="text-left p-4 font-semibold text-medical-blue w-56">Patient Name</th>
                        <th className="text-left p-4 font-semibold text-medical-blue w-32">Order Date</th>
                        <th className="text-left p-4 font-semibold text-medical-blue w-48">Diagnosis</th>
                        <th className="text-left p-4 font-semibold text-medical-blue w-56">Status</th>
                        <th className="text-left p-4 font-semibold text-medical-blue w-40">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map((order) => (
                        <tr 
                          key={order.id}
                          className="border-b border-gray-100 hover:bg-medical-blue-light/30 cursor-pointer transition-colors"
                        >
                          <td className="p-4 font-medium w-28 truncate">{order.patients?.mrn}</td>
                          <td className="p-4 w-56 truncate">{order.patients?.last_name}, {order.patients?.first_name}</td>
                          <td className="p-4 w-32">{formatDate(order.order_date)}</td>
                          <td className="p-4 text-sm text-muted-foreground w-48 truncate" title={order.diagnosis}>{order.diagnosis}</td>
                          <td className="p-4 w-56">
                            {(() => {
                              const statusOptions = getStatusOptions(order.order_status, user?.role || '');
                              
                               // If no status options available (unapproved orders for supervisors), just show badge
                               if (statusOptions.length === 0) {
                                 return getStatusBadge(order.order_status);
                               }
                               
                               // If user has status change options, show dropdown
                               if (statusOptions.length > 0) {
                                return (
                                  <Select 
                                    value={order.order_status} 
                                    onValueChange={(value: Order['order_status']) => handleStatusChange(order.id, value)}
                                  >
                                    <SelectTrigger className="w-52 text-left">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white border shadow-lg z-50">
                                      {statusOptions.map(option => (
                                        <SelectItem key={option.value} value={option.value} className="text-left">
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                );
                              }
                               
                               // Fallback - just show the status badge
                               return getStatusBadge(order.order_status);
                            })()}
                          </td>
                          <td className="p-4 w-40">
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-medical-blue text-medical-blue hover:bg-medical-blue-light"
                                onClick={() => handlePrintOrder(order)}
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                              
                              {/* Edit Button - Role-based visibility */}
                              {canEditOrder(order) && (
                                <Button 
                                  size="sm" 
                                  className="bg-gradient-primary"
                                  onClick={() => handleEditOrder(order)}
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                              )}
                              {/* Cellular Product Inventory Report Icon - Role-based visibility */}
                              {(() => {
                                const userRole = user?.role;
                                const orderStatus = order.order_status;
                                
                                // Supervisors: can see from new_order_received onwards
                                if (userRole === 'supervisor' || userRole === 'admin') {
                                  return (orderStatus === 'new_order_received' || 
                                          orderStatus === 'results_final' ||
                                          orderStatus === 'infusion_ordered' ||
                                          orderStatus === 'infusion_shipped' ||
                                          orderStatus === 'infusion_completed');
                                }
                                
                                // Physicians and Clinicians: can see from results_final onwards
                                if (userRole === 'physician' || userRole === 'clinician') {
                                  return (orderStatus === 'results_final' ||
                                          orderStatus === 'infusion_ordered' ||
                                          orderStatus === 'infusion_shipped' ||
                                          orderStatus === 'infusion_completed');
                                }
                                
                                return false;
                              })() && (
                               <Button 
                                 size="sm" 
                                 variant="outline" 
                                 className="border-green-600 text-green-600 hover:bg-green-50"
                                 onClick={() => handleStatusClick(order)}
                                 title="Open Cellular Product Inventory Report"
                               >
                                 <ClipboardCheck className="h-4 w-4" />
                               </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {currentOrders.length === 0 && !loading && (
                    <div className="text-center py-8 text-muted-foreground">
                      No orders found. Try adjusting your search criteria.
                    </div>
                  )}
                </div>

                {/* Pagination Controls - Always at footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                  <div className="text-sm text-muted-foreground">
                    {totalRecords > 0 ? (
                      <>Showing {startIndex + 1} to {Math.min(endIndex, totalRecords)} of {totalRecords} entries</>
                    ) : (
                      <>No entries found</>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1 || totalRecords === 0}
                      className="border-medical-blue/20 text-medical-blue hover:bg-medical-blue-light disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center space-x-1">
                      {totalPages > 0 && Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = i + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i;
                        } else {
                          pageNumber = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNumber}
                            variant={currentPage === pageNumber ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNumber)}
                            className={`w-8 h-8 p-0 ${
                              currentPage === pageNumber
                                ? "bg-gradient-primary"
                                : "border-medical-blue/20 text-medical-blue hover:bg-medical-blue-light"
                            }`}
                          >
                            {pageNumber}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages || totalRecords === 0}
                      className="border-medical-blue/20 text-medical-blue hover:bg-medical-blue-light disabled:opacity-50"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Order Entry Wizard */}
      <OrderEntryWizard
        isOpen={showOrderWizard}
        onClose={() => setShowOrderWizard(false)}
        onComplete={handleOrderWizardComplete}
        editOrder={editingOrder}
        isReviewMode={!!editingOrder && editingOrder.order_status === 'unapproved' && (user?.role === 'physician' || user?.role === 'admin')}
        onApproveOrder={approveOrder}
      />
      
      {/* Print Report Dialog */}
      <OrderPrintReport
        isOpen={showPrintReport}
        onClose={() => setShowPrintReport(false)}
        order={selectedOrder}
      />
      
      {/* Infusion Form Dialog */}
      <InfusionForm
        orderId={infusionOrderId}
        orderStatus={currentOrderStatus}
        isOpen={showInfusionForm}
        onClose={() => setShowInfusionForm(false)}
        onSubmit={handleInfusionFormSubmit}
        onOrderUpdate={refetch}
      />
    </div>
  );
}